// Webflow IX2 easing curves, as CSS cubic-bezier() strings (for the Web Animations API) and
// as JS functions (for animations scrubbed frame by frame, e.g. Lottie playback).

const CURVES = {
  linear: [0, 0, 1, 1],
  ease: [0.25, 0.1, 0.25, 1],
  easeIn: [0.42, 0, 1, 1],
  easeOut: [0, 0, 0.58, 1],
  easeInOut: [0.42, 0, 0.58, 1],
  outQuad: [0.5, 1, 0.89, 1],
  outQuart: [0.25, 1, 0.5, 1],
  outExpo: [0.19, 1, 0.22, 1],
  inOutExpo: [1, 0, 0, 1],
  inOutQuint: [0.83, 0, 0.17, 1],
} as const;

export type EasingName = keyof typeof CURVES;

export const css = (name: EasingName): string => `cubic-bezier(${CURVES[name].join(', ')})`;

/** Evaluates a CSS cubic-bezier timing function at progress `t` (0..1). */
export function ease(name: EasingName, t: number): number {
  const [x1, y1, x2, y2] = CURVES[name];
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const bez = (a: number, b: number, s: number) => 3 * a * s * (1 - s) ** 2 + 3 * b * s * s * (1 - s) + s ** 3;
  // Solve x(s) = t for s by bisection, then return y(s).
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 30; i++) {
    const mid = (lo + hi) / 2;
    if (bez(x1, x2, mid) < t) lo = mid;
    else hi = mid;
  }
  return bez(y1, y2, (lo + hi) / 2);
}
